/**
 * Editorial homepage hot searches: 3 merchant terms + 7 trending by search count.
 */
(function () {
  const MAX_MERCHANT = 3;
  const MAX_TRENDING = 7;
  const MAX_TOTAL = 10;

  /**
   * @param {string} raw
   * @returns {string[]}
   */
  function parseDelimitedTerms(raw) {
    return (raw || '')
      .split('||')
      .map(function (term) {
        return term.trim();
      })
      .filter(Boolean);
  }

  /**
   * @param {string} term
   * @returns {string}
   */
  function normalizeTerm(term) {
    return term.trim().toLowerCase();
  }

  /**
   * @param {string[]} existing
   * @param {string} term
   * @returns {boolean}
   */
  function hasTerm(existing, term) {
    const key = normalizeTerm(term);
    return existing.some(function (item) {
      return normalizeTerm(item) === key;
    });
  }

  /**
   * @param {unknown} value
   * @returns {{ term: string, count: number }[]}
   */
  /**
   * @param {string} raw
   * @returns {unknown}
   */
  function parseJsonPayload(raw) {
    let parsed = JSON.parse(raw);
    if (typeof parsed === 'string') {
      parsed = JSON.parse(parsed);
    }
    return parsed;
  }

  /**
   * @param {HTMLElement} panel
   * @returns {string}
   */
  function readPopularSearchesRaw(panel) {
    const jsonNode = panel.querySelector('[data-editorial-popular-searches-json]');
    if (jsonNode && jsonNode.textContent) {
      return jsonNode.textContent.trim();
    }
    return (panel.dataset.popularSearches || '').trim();
  }

  function parsePopularEntries(value) {
    if (!value) return [];

    let parsed = value;

    if (typeof parsed === 'string') {
      try {
        parsed = parseJsonPayload(parsed);
      } catch (error) {
        return parseDelimitedTerms(parsed.replace(/\n/g, '||')).map(function (term, index) {
          const parts = term.split(':');
          if (parts.length >= 2) {
            const count = parseInt(parts[parts.length - 1], 10);
            return {
              term: parts.slice(0, -1).join(':').trim(),
              count: Number.isFinite(count) ? count : 0,
            };
          }

          return { term: term, count: MAX_TRENDING - index };
        });
      }
    }

    if (!Array.isArray(parsed)) return [];

    return parsed
      .map(function (entry) {
        if (!entry) return null;

        if (typeof entry === 'string') {
          return { term: entry.trim(), count: 0 };
        }

        const term = (entry.term || entry.query || entry.q || entry.text || '').trim();
        const count = parseInt(entry.count || entry.searches || entry.total || 0, 10);

        if (!term) return null;

        return {
          term: term,
          count: Number.isFinite(count) ? count : 0,
        };
      })
      .filter(Boolean);
  }

  /**
   * @param {HTMLElement} panel
   */
  function initHotSearchPanel(panel) {
    if (!(panel instanceof HTMLElement) || panel.dataset.hotSearchInit === 'true') {
      return;
    }

    panel.dataset.hotSearchInit = 'true';

    const list = panel.querySelector('[data-editorial-hot-search-list]');
    const searchInput =
      panel.querySelector('[data-editorial-search-input]') ||
      panel.closest('.editorial-homepage')?.querySelector('[data-editorial-search-input]');
    const searchUrl = panel.dataset.searchUrl || '/search';

    if (!list) return;

    const merchantTerms = parseDelimitedTerms(panel.dataset.merchantTerms).slice(0, MAX_MERCHANT);
    const fallbackTerms = parseDelimitedTerms(panel.dataset.trendingFallback);
    const popularEntries = parsePopularEntries(readPopularSearchesRaw(panel));

    popularEntries.sort(function (a, b) {
      return b.count - a.count;
    });

    const combined = merchantTerms.slice();
    const trendingPool =
      popularEntries.length > 0
        ? popularEntries.map(function (entry) {
            return entry.term;
          })
        : fallbackTerms;

    trendingPool.forEach(function (term) {
      if (combined.length >= MAX_TOTAL) return;
      if (!hasTerm(combined, term)) {
        combined.push(term.trim());
      }
    });

    const trendingNodes = list.querySelectorAll('[data-editorial-trending-item]');
    trendingNodes.forEach(function (node) {
      node.remove();
    });

    const merchantCount = list.querySelectorAll('[data-editorial-merchant-item]').length;
    const trendingSlots = Math.min(MAX_TRENDING, MAX_TOTAL - merchantCount);

    let renderedTrending = 0;

    trendingPool.forEach(function (term) {
      if (renderedTrending >= trendingSlots) return;
      if (hasTerm(merchantTerms, term)) return;

      const link = document.createElement('a');
      link.href = searchUrl + '?q=' + encodeURIComponent(term);
      link.textContent = '# ' + term;
      link.setAttribute('data-editorial-trending-item', '');
      list.appendChild(link);
      renderedTrending += 1;

      if (!hasTerm(combined, term)) {
        combined.push(term.trim());
      }
    });

    const finalTerms = combined.slice(0, MAX_TOTAL);

    if (searchInput) {
      searchInput.dataset.searchTerms = finalTerms.join('||');
    }

    panel.dispatchEvent(
      new CustomEvent('editorial:hot-searches-ready', {
        bubbles: true,
        detail: { terms: finalTerms },
      })
    );
  }

  function initAll() {
    document.querySelectorAll('[data-editorial-search-panel]').forEach(initHotSearchPanel);
  }

  const scheduleInit = function () {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(initAll, { timeout: 2500 });
    } else {
      window.setTimeout(initAll, 1200);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleInit);
  } else {
    scheduleInit();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
